import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { listProjects, type Project } from './api';
import { useSession } from './auth';

const STORAGE_KEY = 'land-erp.activeProjectId';

type ProjectContextValue = {
  projects: Project[];
  activeProject: Project | null;
  loading: boolean;
  error: string | null;
  setActiveProject: (id: string) => void;
  refresh: () => Promise<void>;
};

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listProjects('active');
      setProjects(rows);
      setActiveIdState((prev) => {
        if (prev && rows.some((p) => p.id === prev)) return prev;
        const next = rows[0]?.id ?? null;
        if (next) window.localStorage.setItem(STORAGE_KEY, next);
        else window.localStorage.removeItem(STORAGE_KEY);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session.isPending && session.data) {
      void load();
    } else if (!session.isPending && !session.data) {
      setProjects([]);
      setActiveIdState(null);
      setLoading(false);
    }
  }, [session.isPending, session.data?.user.id, load]);

  const setActiveProject = useCallback((id: string) => {
    setActiveIdState(id);
    window.localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const activeProject = projects.find((p) => p.id === activeId) ?? null;

  return (
    <ProjectContext.Provider
      value={{ projects, activeProject, loading, error, setActiveProject, refresh: load }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used inside ProjectProvider');
  return ctx;
}
